import { message, Skeleton, Spin, Switch, Table, Tooltip, Tour } from "antd";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import onBoardingPhoneSwitch from "../../assets/gif/onBoardingPhoneSwitch.gif";
import { AppContext } from "../../context/AppContext";
import { LoadingOutlined } from "@ant-design/icons";
import useHttp from "../../hooks/useHttp";
import "../../voice/assets/css/voiceConfigPhone.css";
import { initialEdges, initialNodes, nodeForDirectRoute } from "../../voice/components/nodes-edges";
import VoiceConfiguration from "../../voice/components/voice/VoiceConfiguration";
import { useVoiceContext } from "../../voice/context/VoiceContext";
import { useCallFlow } from "../../voice/hooks/useCallFlow";
import { useVoiceNumbers } from "../../voice/hooks/useVoiceNumbers";
import { getCurrentFeature } from "../../voice/utils/VoiceUtils";
import { ConfirmAlertModal, EmptyListView, UpgradeAlertPopup } from "../utils/CommonVessels";
import { urlParams } from "./../../constants/AppConstants";

const VoiceConfigPhone = ({ rightServiceObj, setShowFinishButton }) => {
  const { getCallFlowList } = useCallFlow();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [voiceNumberEnabled, setVoiceNumberEnabled] = useState(false);
  const [callProperties, setCallProperties] = useVoiceContext();
  const [upgradeAlertPopup, setUpgradeAlertPopup] = useState(false);
  const ref1 = useRef(null);
  const { getVoiceSavedNumbers, deleteVoiceSavedNumber, addNumber } = useVoiceNumbers();
  const ref3 = useRef(null);
  const [fetchingNumberList, setFetchingNumberList] = useState(true);
  const [phoneNumberList, setPhoneNumberList] = useState([]);
  const [savedNumbers, setSavedNumbers] = useState([]);
  const { fetchData } = useHttp();
  const [isEnabled, setIsEnabled] = useState({});
  const [callFlows, setCallFlows] = useState([]);
  const [switchLoading, setSwitchLoading] = useState({});
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [loadingSavedNumbers, setLoadingSavedNumbers] = useState(false);
  const [loadingCallFlows, setLoadingCallFlows] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [phoneConfigurationSettingPage, setPhoneConfigurationSettingPage] = useState(false);
  const [selectedNumberData, setSelectedNumberData] = useState(null);
  const [deletePhoneModal, setDeletePhoneModal] = useState({
    open: false,
    phoneNumber: null,
  });
  const [departmentsList, setDepartmentsList] = useState([]);
  const [appProperties] = useContext(AppContext);
  const voiceIntegrations = useMemo(
    () => appProperties?.installedApps?.filter((integration) => integration.right.serviceType === "VOICE"),
    [appProperties]
  );
  const voiceIntegId = useMemo(() => voiceIntegrations?.[0]?.integProps?.integId, [voiceIntegrations]);

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  let onBoardingPage = urlParams.get("onBoardingPage");
  const [tourStart, setTourStart] = useState(onBoardingPage === "true");
  useEffect(() => {
    if (savedNumbers) {
      setCallProperties((prev) => ({
        ...prev,
        savedNumbers: savedNumbers,
      }));
    }
  }, [savedNumbers]);

  useEffect(() => {
    if (!loadingPhoneNumbers && !loadingSavedNumbers && !loadingCallFlows) {
      setFetchingNumberList(false);
    }
  }, [loadingPhoneNumbers, loadingSavedNumbers, loadingCallFlows]);

  const onBoardingTourSteps = [
    {
      description: (
        <>
          <img alt="onBoardingPhoneSwitch.gif" style={{ height: "70px" }} src={onBoardingPhoneSwitch} />
        </>
      ),
      placement: "right",
      target: () => ref1.current,
    },
  ];

  const handleClickOutside = (event) => {
    if (ref1.current && !ref1.current.contains(event.target)) {
      setTourStart(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const columns = [
    {
      title: (
        <div className="d-flex flex-row">
          <div className="ms-2">Numbers</div>
          <Tooltip title={"Only voice-enabled numbers will be listed here"}>
            <div className="call-action-icons voice-violet-info-icon" />
          </Tooltip>
        </div>
      ),
      dataIndex: "numbers",
      key: "numbers",
      width: 150,
    },
    {
      title: <div className="me-4">Call Flow</div>,
      dataIndex: "callFlow",
      key: "callFlow",
      width: 200,
    },
    // {
    //   title: <div className="">Hold Music</div>,
    //   dataIndex: "holdMusic",
    //   key: "holdMusic",
    // },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 80,
    },
  ];

  const handleSwitchChange = async (checked, phoneNumber) => {
    setSwitchLoading((prev) => ({ ...prev, [phoneNumber]: true }));
    const nodes = [...initialNodes, ...nodeForDirectRoute];
    const initialNodesWithUniqueNodeId = nodes?.map((node) => {
      const newNodeId = uuidv4();
      return {
        ...node,
        nodeId: newNodeId,
      };
    });

    const payload = {
      phoneNumber: phoneNumber,
      nodes: initialNodesWithUniqueNodeId,
      edges: initialEdges,
      emailId: appProperties?.currentUser?.email,
    };
    try {
      if (checked) {
        setIsEnabled((prev) => ({ ...prev, [phoneNumber]: true }));

        const response = await addNumber(payload, voiceIntegId);
        if (response) {
          if (response) {
            setTourStart(false);
            setCallProperties((prev) => ({
                ...prev,
                savedNumbers: [...(prev?.savedNumbers ?? []), response],
            }));

            setShowFinishButton(true);
            setVoiceNumberEnabled(true);
          }
          goToNextStep();
          setSavedNumbers((prev) => [...prev, response]);
          message.success("Phone number saved successfully.");
        } else {
          message.error("An error occurred while saving the phone number. Please try again.");
        }
      } else {
        setDeletePhoneModal({ open: true, phoneNumber: phoneNumber });
        setIsEnabled((prev) => ({ ...prev, [phoneNumber]: false }));
      }
    } catch (error) {
      console.error("Error in handling phone number:", error);
      message.error("An error occurred while processing your request.");
    } finally {
      setSwitchLoading((prev) => ({ ...prev, [phoneNumber]: false }));
    }
  };

  const doOnCancel = () => {
    setDeletePhoneModal({ open: false, phoneNumber: null });
  };
  const doOnConfirm = async () => {
    try {
      await deleteVoiceSavedNumber(deletePhoneModal?.phoneNumber).then((response) => {
        setSavedNumbers((prevNumberData) => prevNumberData.filter((num) => num.phoneNumber !== deletePhoneModal?.phoneNumber));
        message.success("Phone number deleted successfully.");
      });
    } catch (error) {
      console.error("Error while deleting phone number:", error);
      message.error("An error occurred while deleting the phone number.");
    }
    setDeletePhoneModal({ open: false, phoneNumber: null });
  };

  useEffect(() => {
    const featureName = getCurrentFeature(appProperties);
    if (featureName === "Message") {
      setUpgradeAlertPopup(true);
    } else {
      if (voiceIntegId) {
        setFetchingNumberList(true);
        setLoadingPhoneNumbers(true);
        fetchData(`omessage/${voiceIntegId}/numbers?capabilities=voice`, "GET", null, appProperties)
          .then((response) => {
            const responseData = JSON.parse(response);
            const nestedData = JSON.parse(responseData?.data?.data || "{}");
            const phoneNumbers = nestedData?.phone || [];

            setPhoneNumberList(phoneNumbers);
          })
          .catch((error) => console.error("Error fetching or parsing data:", error))
          .finally(() => setLoadingPhoneNumbers(false));
      }
    }
  }, [appProperties, fetchData, voiceIntegId]);

  useEffect(() => {
    const loadCallFlows = async () => {
      try {
        setLoadingCallFlows(true);
        const callFlowResponse = await getCallFlowList(voiceIntegId);
        if (callFlowResponse && Array.isArray(callFlowResponse) && callFlowResponse?.length > 0) {
          setCallFlows(callFlowResponse);
        }
      } catch (error) {
        console.error("Error parsing response data:", error);
      } finally {
        setLoadingCallFlows(false);
      }
    };
    loadCallFlows();
  }, [getCallFlowList, voiceIntegId, voiceNumberEnabled]);

  useEffect(() => {
    if (voiceIntegId && !upgradeAlertPopup) {
      setLoadingSavedNumbers(true);
      getVoiceSavedNumbers("all")
        .then((response) => {
          if (response) {
            const numbersArray = response?.numberVoiceConfigurationEntities || [];
            setSavedNumbers(numbersArray);
            setDepartmentsList(response?.departments || []);
            setCallProperties((prev) => ({
              ...prev,
              savedNumbers: numbersArray,
            }));
          }
        })
        .catch((err) => {
        setSavedNumbers([]);
      })
        .finally(() => setLoadingSavedNumbers(false));
    }
  }, [getVoiceSavedNumbers, voiceIntegId]);

  useEffect(() => {
    if (savedNumbers && appProperties?.providerCommonObj?.handleSavedNumberProcessingForOnboardingPageRedirect) {
        appProperties?.providerCommonObj?.handleSavedNumberProcessingForOnboardingPageRedirect(appProperties, voiceIntegId, location, navigate, searchParams, savedNumbers);
    }
  }, [savedNumbers, appProperties?.providerCommonObj]);

  const findCallFlowNameById = (callFlowId, savedNumber) => {
    const flow = callFlows.find((flow) => flow.callFlowId === callFlowId);
    return flow?.callFlowName ?? savedNumber?.callFlowName ?? null;
  };

  const navigateCallFlow = (callFlowId, phoneNumber) => {
    try {
      const editCallFlowEndpoint = `/settings/callFlow/edit/${callFlowId}/phoneNumber/${encodeURIComponent(phoneNumber)}`;
      navigate(editCallFlowEndpoint + location.search);
    } catch (error) {}
  };

  const handlePhoneNumberEditClick = (phoneNumber, numberData) => {
    setPhoneConfigurationSettingPage(true);
    setSelectedPhoneNumber(phoneNumber);
    setSelectedNumberData(numberData);
  };

  const dataSource = phoneNumberList.map((num, index) => {
    const savedNumber = Array.isArray(savedNumbers) ? savedNumbers.find((element) => element?.phoneNumber === num) : null;
    const callFlowName = savedNumber ? findCallFlowNameById(savedNumber.callFlowId, savedNumber) : "-";
    return {
      key: index,
      numbers: (
        <div className="d-flex flex-row align-items-center">
          <div className="hs-fw-500 me-1">
            {num}
          </div>
          {callFlowName !== "-" && (
            <div onClick={() => handlePhoneNumberEditClick(num, savedNumber)} className="number-edit-icon actionIconsSprite" />
          )}
        </div>
      ),
      callFlow: (
        <div className="hs-fw-500 hs-fs-13" style={{ cursor: "default" }}>
          {callFlowName === null ? <LoadingOutlined spin /> : callFlowName}
          {callFlowName !== "-" && callFlowName !== null && (
            <span
              ref={ref3}
              className={
                isEnabled?.[num] || (Array.isArray(savedNumbers) && savedNumbers.some((element) => element?.phoneNumber === num))
                  ? "enabled-span"
                  : "disabled-span"
              }
              onClick={() => {
                navigateCallFlow(savedNumber?.callFlowId, num, savedNumber);
              }}
            >
              {" "}
              Edit
            </span>
          )}
        </div>
      ),
      status: (
        <Switch
          ref={index === 0 ? ref1 : null}
          loading={switchLoading[num]}
          checked={Array.isArray(savedNumbers) && savedNumbers.some((element) => element?.phoneNumber === num)}
          className="addPhoneSwitch"
          size="small"
          onClick={(e) => handleSwitchChange(e, num)}
        />
      ),
    };
  });

  const goToNextStep = () => {
    setCurrentStep((prevStep) => (prevStep + 1 < onBoardingTourSteps.length ? prevStep + 1 : 0));
  };

  return (
    <>
      <div className="d-flex align-items-center hs-border-grey  hs-border-6 hs-lg-h-700-px" style={{ backgroundColor: "#fff" }}>
        <div className="w-100 mt-1 ms-2 h-100 mt-lg-3">
          {fetchingNumberList ? (
            <Skeleton className="w-100% mt-lg-4" paragraph={{ rows: 4 }} active />
          ) : phoneConfigurationSettingPage ? (
            <VoiceConfiguration
              phoneNumber={selectedPhoneNumber}
              setPhoneConfigurationSettingPage={setPhoneConfigurationSettingPage}
              selectedNumberData={selectedNumberData}
              setSavedNumbers={setSavedNumbers}
              departmentsList={departmentsList}
            />
          ) : (
            <div className="">
              <Table
                scroll={{ y: 480 }}
                pagination={false}
                className="m-0 voiceNumberTable h-100 phoneAddTable overflow-hidden"
                columns={columns}
                dataSource={dataSource}
                locale={{
                  emptyText: (
                    <EmptyListView
                      icon={"callCardIcons emptyVoiceCallListView display-inline-block"}
                      tagLineOne={"No Numbers"}
                      tagLineTwo={"You have no numbers in Twilio or no numbers available."}
                      tagHeight={"40vh"}
                    />
                  ),
                }}
              />
              <Tour
                rootClassName="voice-tour"
                closeIcon={false}
                open={tourStart}
                onClose={() => setTourStart(false)}
                steps={onBoardingPage === "true" ? onBoardingTourSteps : onBoardingTourSteps}
                // current={currentStep}
                // onChange={setCurrentStep}
              />

              <ConfirmAlertModal
                onCancel={doOnCancel}
                open={deletePhoneModal.open}
                onConfirm={doOnConfirm}
                centered={true}
                alertIcon={<div className="phoneLogoSprites delete-icon me-4" />}
                titleContent={"Are you sure you want to disconnect?"}
                modalContent={`If you proceed, you won't be able to make or receive calls anymore, and you might miss important calls from customers. Confirm if you still want to proceed.`}
                buttonConfirm={"Yes, confirm"}
              />
            </div>
          )}
        </div>
      </div>

      {upgradeAlertPopup && <UpgradeAlertPopup upgradeAlertPopup={upgradeAlertPopup} setUpgradeAlertPopup={setUpgradeAlertPopup} />}
    </>
  );
};

export default VoiceConfigPhone;
